<template>
  <div>
    <!-- Add/Edit Complaint Modal -->
    <b-modal
      v-model="isComplaintModalVisible"
      title="Add Complaint"
      size="lg"
      @hidden="resetForm"
      centered
    >
      <b-form @submit.prevent>
        <b-row>
          <b-col md="12" v-if="complaintObj.sID === 0">
            <b-input-group>
              <b-input-group-prepend
                is-text
                @click="loadSearch()"
                class="cursor-pointer"
              >
                <feather-icon icon="SearchIcon" />
              </b-input-group-prepend>
              <b-form-input
                v-model="stdQuery"
                @keyup.enter="loadSearch()"
                placeholder="Search Student"
              />
            </b-input-group>

            <b-table
              class="mt-1"
              show-empty
              ref="selectableTable"
              :items="students"
              :fields="stdFields"
              :busy="studentLoading"
              responsive
              hover
              selectable
              select-mode="single"
              @row-selected="getStudent"
              small
            >
              <template #table-busy>
                <div class="text-center my-3">
                  <b-spinner
                    style="width: 3rem; height: 3rem"
                    type="grow"
                    variant="primary"
                    label="Spinning"
                  ></b-spinner>
                </div>
              </template>
              <template #head(details)="data">
                <div class="text-center">
                  <span>{{ data.label }}</span>
                </div>
              </template>
              <template #cell(details)="data">
                <div class="d-flex align-items-center">
                  <b-avatar
                    size="42"
                    :src="data.item.picture"
                    variant="light-secondary"
                  />
                  <div class="chat-info flex-grow-1 ml-2">
                    <h5 class="mb-0">{{ data.item.name }}</h5>
                    <p class="card-text text-truncate">
                      {{ data.item.meta_class }}
                    </p>
                  </div>
                </div>
              </template>
            </b-table>
          </b-col>

          <b-col md="12" v-else>
            <b-form-group label="Student">
              <b-col class="mb-1 d-flex align-items-center">
                <div class="d-flex align-items-center">
                  <b-avatar
                    size="42"
                    :src="currentStd.picture"
                    variant="light-secondary"
                  />
                  <div class="chat-info flex-grow-1 ml-2">
                    <h5 class="mb-0">{{ currentStd.name }}</h5>
                    <p class="card-text text-truncate">
                      {{ currentStd.meta_class }}
                    </p>
                  </div>
                </div>
                <b-button
                  @click="complaintObj.sID = 0"
                  class="btn-icon ml-3"
                  v-b-tooltip.hover.right
                  title="Remove"
                  variant="outline-danger"
                >
                  <feather-icon icon="XIcon" />
                </b-button>
              </b-col>
            </b-form-group>
          </b-col>

          <template v-if="complaintObj.sID !== 0">
            <b-col md="6">
              <b-form-group label="Category" label-for="category">
                <v-select
                  v-model="complaintObj.categoryID"
                  :options="categories"
                  label="category"
                  :reduce="(cat) => cat.id"
                  placeholder="Select Category"
                  :clearable="false"
                />
              </b-form-group>
            </b-col>

            <b-col md="6">
              <b-form-group label="Complaint for" label-for="about">
                <b-form-input
                  id="about"
                  v-model="complaintObj.about"
                  placeholder="Enter complaint for"
                />
              </b-form-group>
            </b-col>

            <b-col md="12">
              <b-form-group label="Details" label-for="details">
                <b-form-textarea
                  id="details"
                  v-model="complaintObj.details"
                  placeholder="Enter complaint details"
                  rows="3"
                />
              </b-form-group>
            </b-col>

            <b-col md="6">
              <b-form-group label="Complaint By" label-for="complaintBy">
                <b-form-input
                  id="complaintBy"
                  v-model="complaintObj.complaintBy"
                  placeholder="Enter name"
                />
              </b-form-group>
            </b-col>

            <b-col md="12">
              <hr />
              <h5>Checklists</h5>

              <div
                v-for="(checklist, checklistIndex) in complaintObj.checklists"
                :key="checklistIndex"
                class="mb-3"
              >
                <b-card>
                  <div class="mb-2">
                    <b-progress
                      :value="calculateChecklistProgress(checklist)"
                      max="100"
                      class="mb-1"
                      variant="success"
                    ></b-progress>
                    <small class="d-flex justify-content-between">
                      <span>Progress</span>
                      <span>{{ calculateChecklistProgress(checklist) }}%</span>
                    </small>
                  </div>

                  <b-form-group label="">
                    <b-form-input
                      v-model="checklist.title"
                      placeholder="Enter checklist title"
                      class="mb-1"
                    />
                  </b-form-group>

                  <div
                    v-for="(item, itemIndex) in checklist.items"
                    :key="itemIndex"
                    class="d-flex align-items-center mb-1"
                  >
                    <b-form-checkbox v-model="item.completed" class="mr-2" />
                    <div
                      class="flex-grow-1 cursor-pointer"
                      @click="editItem(checklistIndex, itemIndex)"
                    >
                      {{ item.text }}
                    </div>
                    <div
                      class="d-flex align-items-center ml-2"
                      v-if="item.assignee"
                    >
                      <b-avatar
                        size="24"
                        :src="getAssigneeProfile(item.assignee)"
                        variant="light-secondary"
                        class="mr-1"
                      />
                      <span>{{ getAssigneeName(item.assignee) }}</span>
                    </div>
                    <div
                      class="d-flex align-items-center ml-2"
                      v-if="item.dueDate"
                    >
                      <feather-icon icon="ClockIcon" size="16" class="mr-1" />
                      <span>{{
                        new Date(item.dueDate).toLocaleDateString()
                      }}</span>
                    </div>
                  </div>

                  <div class="mt-1">
                    <b-form-input
                      v-model="newItemTexts[checklistIndex]"
                      placeholder="Add an item"
                      class="mb-2"
                    />
                    <div
                      class="d-flex justify-content-between align-items-center"
                    >
                      <div>
                        <b-button
                          variant="primary"
                          @click="addChecklistItem(checklistIndex)"
                        >
                          {{ checklist.editingItem !== null ? "Save" : "Add" }}
                        </b-button>
                        <b-button
                          variant="secondary"
                          class="ml-1"
                          @click="resetNewItem(checklistIndex)"
                        >
                          Cancel
                        </b-button>
                      </div>
                      <div class="d-flex align-items-center">
                        <div class="d-flex align-items-center ml-2">
                          <feather-icon
                            icon="UserIcon"
                            size="16"
                            class="mr-1"
                          />
                          <v-select
                            v-model="newItemAssignee[checklistIndex]"
                            :options="staffMembers"
                            label="name"
                            :reduce="(staff) => staff.id"
                            placeholder="Assign"
                            class="item-assign-select"
                          />
                        </div>
                        <div class="d-flex align-items-center ml-2">
                          <feather-icon
                            icon="ClockIcon"
                            size="16"
                            class="mr-1"
                          />
                          <flat-pickr
                            v-model="newItemDueDate[checklistIndex]"
                            class="form-control item-date-picker"
                            placeholder="Due date"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </b-card>
              </div>

              <div class="text-center mt-1">
                <b-button
                  variant="outline-primary"
                  class="btn-icon"
                  @click="addChecklist"
                >
                  <feather-icon icon="PlusIcon" class="mr-50" />
                  <span>Add Checklist</span>
                </b-button>
              </div>
            </b-col>
          </template>
        </b-row>
      </b-form>

      <template #modal-footer>
        <b-button
          variant="primary"
          @click="saveComplaint"
          :disabled="isSubmitting"
        >
          <b-spinner small v-if="isSubmitting" />
          <span>Save</span>
        </b-button>
      </template>
    </b-modal>

    <b-card>
      <b-row>
        <b-col md="12" class="mb-2">
          <b-button variant="primary" @click="showAddComplaintModal">
            <feather-icon icon="PlusIcon" class="mr-50" />
            <span>Add Complaint</span>
          </b-button>
        </b-col>

        <b-col md="12">
          <b-table
            :items="complaints"
            :fields="fields"
            responsive
            show-empty
            hover
            small
            :busy="dataLoading"
          >
            <template #table-busy>
              <div class="text-center my-3">
                <b-spinner
                  style="width: 3rem; height: 3rem"
                  type="grow"
                  variant="primary"
                  label="Spinning"
                ></b-spinner>
              </div>
            </template>

            <template #cell(about)="data">
              {{ data.item.us.about }}
            </template>
            <template #cell(complaintBy)="data">
              {{ data.item.us.complaintBy }}
            </template>
            <template #cell(date)="data">
              <b-badge variant="light-primary">
                {{
                  new Date(data.item.us.date).toLocaleDateString("en-UK", {
                    year: "numeric",
                    day: "numeric",
                    month: "short",
                  })
                }}
              </b-badge>
            </template>

            <template #cell(status)="data">
              <b-badge variant="light-primary">
                <span>{{ data.item.us.status }}</span>
              </b-badge>
            </template>
            <template #cell(actions)="data">
              <b-button
                variant="primary"
                class="btn-icon"
                @click="viewComplaint(data.item)"
              >
                <feather-icon icon="EyeIcon" size="16" />
              </b-button>
            </template>
          </b-table>
        </b-col>
      </b-row>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BModal,
  BForm,
  BFormGroup,
  BFormTextarea,
  BProgress,
  BSpinner,
  BFormCheckbox,
  BBadge,
  BInputGroup,
  BInputGroupPrepend,
  BAvatar,
  VBTooltip,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { mapActions } from "vuex";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";

export default {
  name: "Complaints",
  components: {
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BModal,
    BForm,
    BFormGroup,
    BFormTextarea,
    BProgress,
    BSpinner,
    BFormCheckbox,
    BBadge,
    BInputGroup,
    BInputGroupPrepend,
    BAvatar,
    vSelect,
    flatPickr,
  },
  directives: {
    "b-tooltip": VBTooltip,
  },
  data() {
    return {
      complaints: [],
      staffMembers: [],
      categories: [],
      dataLoading: false,
      isComplaintModalVisible: false,
      isSubmitting: false,
      newItemTexts: {},
      newItemAssignee: {},
      newItemDueDate: {},
      students: [],
      studentLoading: false,
      stdQuery: "",
      currentStd: {},
      stdFields: [{ label: "Students", key: "details" }],
      fields: [
        { key: "about", label: "About" },
        { key: "complaintBy", label: "Complaint By" },
        { key: "date", label: "Date" },
        { key: "status", label: "Status" },
        { key: "actions", label: "Actions" },
      ],
      complaintObj: this.getDefaultComplaintObj(),
    };
  },
  created() {
    this.loadComplaints();
    this.loadStaffMembers();
    this.loadCategories();
  },
  methods: {
    ...mapActions(["get", "post"]),

    getDefaultComplaintObj() {
      return {
        id: 0,
        campusID: this.$store.state.userData.cId,
        categoryID: 0,
        sID: 0,
        about: "",
        details: "",
        date: "",
        status: "pending",
        complaintBy: "",
        note: "",
        resolvedBy: "",
        resolvedOn: null,
        assignee: null,
        checklists: [],
      };
    },

    async fetchCurrentDate() {
      var obj = {
        url: this.$store.state.domain + "Fees/GetDate",
        token: this.$store.state.userData.token,
      };
      let res = await this.get(obj);
      return res && !Array.isArray(res) ? res : new Date().toJSON();
    },

    getAssigneeName(assigneeId) {
      const assignee = this.staffMembers.find((s) => s.id === assigneeId);
      return assignee ? assignee.name : "-";
    },

    getAssigneeProfile(assigneeId) {
      const assignee = this.staffMembers.find((s) => s.id === assigneeId);
      return assignee ? assignee.picture : null;
    },

    async showAddComplaintModal() {
      this.complaintObj = this.getDefaultComplaintObj();
      this.complaintObj.date = await this.fetchCurrentDate();
      this.isComplaintModalVisible = true;
    },

    addChecklist() {
      this.complaintObj.checklists.push({
        title: "",
        items: [],
        showAddItem: true,
        editingItem: null,
      });
      const index = this.complaintObj.checklists.length - 1;
      this.$set(this.newItemTexts, index, "");
      this.$set(this.newItemAssignee, index, null);
      this.$set(this.newItemDueDate, index, null);
      // Add default first item
      this.addChecklistItem(index);
    },

    addChecklistItem(checklistIndex) {
      if (!this.newItemTexts[checklistIndex]) return;

      if (this.complaintObj.checklists[checklistIndex].editingItem !== null) {
        // Update existing item
        const itemIndex =
          this.complaintObj.checklists[checklistIndex].editingItem;
        this.complaintObj.checklists[checklistIndex].items[itemIndex] = {
          text: this.newItemTexts[checklistIndex],
          completed: false,
          assignee: this.newItemAssignee[checklistIndex],
          dueDate: this.newItemDueDate[checklistIndex],
        };
        this.complaintObj.checklists[checklistIndex].editingItem = null;
      } else {
        // Add new item
        this.complaintObj.checklists[checklistIndex].items.push({
          text: this.newItemTexts[checklistIndex],
          completed: false,
          assignee: this.newItemAssignee[checklistIndex],
          dueDate: this.newItemDueDate[checklistIndex],
        });
      }
      this.resetNewItem(checklistIndex);
      this.complaintObj.checklists[checklistIndex].showAddItem = false;
    },

    editItem(checklistIndex, itemIndex) {
      const item =
        this.complaintObj.checklists[checklistIndex].items[itemIndex];
      this.newItemTexts[checklistIndex] = item.text;
      this.newItemAssignee[checklistIndex] = item.assignee;
      this.newItemDueDate[checklistIndex] = item.dueDate;
      this.complaintObj.checklists[checklistIndex].editingItem = itemIndex;
      this.complaintObj.checklists[checklistIndex].showAddItem = true;
    },

    removeChecklistItem(checklistIndex, itemIndex) {
      this.complaintObj.checklists[checklistIndex].items.splice(itemIndex, 1);
    },

    removeChecklist(checklistIndex) {
      this.complaintObj.checklists.splice(checklistIndex, 1);
    },

    calculateProgress(checklists) {
      if (!checklists || checklists.length === 0) return 0;

      let totalItems = 0;
      let completedItems = 0;

      checklists.forEach((checklist) => {
        checklist.items.forEach((item) => {
          totalItems++;
          if (item.completed) completedItems++;
        });
      });

      return totalItems === 0
        ? 0
        : Math.round((completedItems / totalItems) * 100);
    },

    calculateChecklistProgress(checklist) {
      if (!checklist.items || checklist.items.length === 0) return 0;

      const totalItems = checklist.items.length;
      const completedItems = checklist.items.filter(
        (item) => item.completed
      ).length;

      return Math.round((completedItems / totalItems) * 100);
    },

    async saveComplaint() {
      console.log(this.complaintObj);
      return;
      try {
        this.isSubmitting = true;
        const response = await this.post({
          url: `${this.$store.state.domain}SchoolComplaints?db=${this.$store.state.userData.db}`,
          body: this.complaintObj,
          token: this.$store.state.userData.token,
        });

        if (response) {
          this.$bvToast.toast("Complaint saved successfully", {
            title: this.$t("Success"),
            variant: "success",
            solid: true,
          });
          this.isComplaintModalVisible = false;
          this.loadComplaints();
        }
      } catch (error) {
        console.error("Error saving complaint:", error);
      } finally {
        this.isSubmitting = false;
      }
    },

    viewComplaint(complaint) {
      // Implement view complaint logic
    },

    resetForm() {
      this.complaintObj = this.getDefaultComplaintObj();
    },

    async loadCategories() {
      var obj = {
        url:
          this.$store.state.domain +
          "ComplaintCategories?db=" +
          this.$store.state.userData.db +
          "&cId=" +
          this.$store.state.userData.cId,
        token: this.$store.state.userData.token,
      };
      this.categories = await this.get(obj);
    },

    async loadStaffMembers() {
      try {
        let response = await this.get({
          url:
            this.$store.state.domain +
            "staff/filter?db=" +
            this.$store.state.userData.db +
            "&cId=" +
            this.$store.state.userData.cId +
            "&dptID=0&status=present",
          token: this.$store.state.userData.token,
        });
        this.staffMembers = response || [];
      } catch (error) {
        console.error("Error loading staff members:", error);
      }
    },

    async loadComplaints() {
      try {
        this.dataLoading = true;
        const response = await this.get({
          url: `${this.$store.state.domain}SchoolComplaints/LoadData?db=${this.$store.state.userData.db}&cId=${this.$store.state.userData.cId}`,
          token: this.$store.state.userData.token,
        });
        this.complaints = response || [];
      } catch (error) {
        console.error("Error loading complaints:", error);
      } finally {
        this.dataLoading = false;
      }
    },

    async loadSearch() {
      this.studentLoading = true;
      let response = await this.get({
        url:
          this.$store.state.domain +
          "Students/Search?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId +
          "&keyword=" +
          this.stdQuery,
        token: this.$store.state.userData.token,
      });
      this.students = response || [];
      this.studentLoading = false;
    },

    getStudent(row) {
      if (!row || row.length === 0) return;
      this.complaintObj.sID = row[0].id;
      this.currentStd = row[0];
    },

    resetNewItem(checklistIndex) {
      this.newItemTexts[checklistIndex] = "";
      this.newItemAssignee[checklistIndex] = null;
      this.newItemDueDate[checklistIndex] = null;
      this.complaintObj.checklists[checklistIndex].showAddItem = false;
      this.complaintObj.checklists[checklistIndex].editingItem = null;
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";

.item-assign-select {
  min-width: 150px;
  .vs__dropdown-toggle {
    padding: 0.2rem 0.8rem;
    border: 1px solid #d8d6de;
    border-radius: 0.357rem;
  }
}

.item-date-picker {
  width: 150px;
  padding: 0.2rem 0.8rem;
  height: auto;
}
</style>
